import Axios, { InternalAxiosRequestConfig } from 'axios';
import accountContext from '../account/accountContext';
import { handle403Error } from './errorHandler';
import { HOST_GATE_ERROR_CODES, isRegistrationHostRefusal, stashPilotHostGate } from '../account/pilotHostGate';
import { ApiErrorDetails, SerializedError } from '../shared/models/errorHandlingModels';
import { AccountRoutePaths } from '../account/routes';

const addAuthentication = (config: InternalAxiosRequestConfig) => {
  const token = accountContext.accessToken;
  if (token) {
    config.headers.authorization = `Bearer ${token}`;
    return;
  }
};

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  addAuthentication(config);
  config.headers.Accept = 'application/json';
  config.headers['X-UI-Language'] = localStorage.getItem('i18nextLng') || 'sk';

  return config;
};

// LOGOUT-AMPLIFIER fix (2026-04-30): bot-proxy endpoints can return 401 for
// reasons unrelated to the user's MachineID session (bot-side JWT signature
// mismatch, route inconsistency, bot auth misconfig). Treating those as
// session-expiration would log the user out of the whole app — see the
// `feedback_report_bot_bugs_not_mask.md` rationale. We carve those URLs
// out here so the error still propagates to the chat caller (chatBotSlice
// already surfaces it as chat-error UX), but the global logout/redirect
// is skipped. Real auth-endpoint 401s (/v1/auth/..., /v1/account/...)
// continue to logout as before.
const isBotProxyUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  // Match both relative (`/v1/bot/...`) and absolute (`https://host/v1/bot/...`)
  // request URLs — axios may pass either depending on whether baseURL was
  // applied at config time.
  return /(^|\/)v1\/bot\//.test(url);
};

// The two sign-in host gates return 403 for a user whose credentials were CORRECT but who is on
// the wrong hostname: PILOT_HOST_NOT_ALLOWED (MidasPilot outside PilotAccess:AllowedSignInHosts)
// and HOST_NOT_ALLOWED (any user off their tenant's home surface, HOST-POLICY-TENANT-HOME).
// Neither may go through handle403Error: on the sign-in page that would stack the generic
// "contact your administrator" permission toast on top of the form, and on a /public/ path
// it would log the user out and bounce them with a returnUrl — both wrong for what is really
// a "you are on the wrong address" notice.
const isHostGateError = (error: any): boolean =>
  error?.response?.status === 403 &&
  HOST_GATE_ERROR_CODES.has(error?.response?.data?.errorCode);

// COM-REGISTRATION-INVITE-ONLY: the Customers registration refusal (403
// REGISTRATION_NOT_AVAILABLE_ON_HOST) is not a permission denial either — the caller has no
// account yet, so the "Contact your administrator" access-denied toast is meaningless and would
// stack on top of the invite-only page the registration slices route to. It is deliberately NOT
// added to HOST_GATE_ERROR_CODES: membership there ALSO triggers the stash + forced logout +
// redirect-to-sign-in branch below, which is the wrong destination for this one (there is no
// session to end, and the right landing place is the invite-only page).
const isRegistrationSurfaceRefusal = (error: any): boolean =>
  error?.response?.status === 403 &&
  isRegistrationHostRefusal(error?.response?.data?.errorCode);

// The INTERACTIVE sign-in calls ONLY — `/v1/account/login` and `/v1/account/login/pin`.
// These are the two whose 403 the login slice renders inline, in the form the user is
// already looking at, so the interceptor must not log out or redirect for them.
// `/v1/account/token/refresh` is deliberately EXCLUDED: a PILOT_HOST_NOT_ALLOWED there
// means an already-held session is over, which must take the stash + logout + redirect
// path below. (Review fix: the old regex classified refresh as a sign-in call, so a
// refused refresh silently did nothing and contradicted the comment at the branch.)
const isSignInUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  return /(^|\/)v1\/account\/login(\/pin)?(?:[?#]|$)/.test(url);
};

const responseErrorInterceptor = (error: any) => {
  const isBotProxy = isBotProxyUrl(error?.config?.url);

  if (error?.response?.status === 401) {
    if (!isBotProxy) {
      accountContext.logout();
      window.location.href = "/";
    }
  }

  // Bot-proxy 403s are NOT global permission denials — they come from the
  // bot's own auth pipeline (whitelist, bot JWT, etc.) and surfacing the
  // "Contact your administrator" toast for them is the wrong UX. The chat /
  // feedback caller handles the error inline.
  if (
    error?.response?.status === 403 &&
    !isBotProxy &&
    !isHostGateError(error) &&
    !isRegistrationSurfaceRefusal(error)
  ) {
    handle403Error(error?.response?.data);
  }

  if (isHostGateError(error)) {
    const allowedHost = error?.response?.data?.details?.allowedHost;

    // On the sign-in calls the login slice renders the notice inline, in the form the
    // user is already looking at — nothing to redirect. Anywhere else (a session already
    // held on the wrong host, or a token refresh) the session is over: stash the notice,
    // log out and land on the sign-in page, which picks the notice back up. We never
    // auto-navigate to the allowed host, and no token travels anywhere.
    if (!isSignInUrl(error?.config?.url)) {
      stashPilotHostGate(allowedHost);
      accountContext.logout();
      window.location.href = AccountRoutePaths.signIn;
    }
  }

  // Bot-proxy carve-out: preserve the original AxiosError so consumers can
  // read `error.response.status` (needed for 409 duplicate_feedback soft
  // success on the feedback endpoint, and similar status-driven branching).
  // The default SerializedError transform below drops `response`, which made
  // the v3 feedback slice see `status: 0` for every 4xx. (Bug fix 2026-05-23.)
  // Note: all bot-proxy axios callers (feedback, history, threads, context)
  // now receive AxiosError instead of SerializedError. `error.message` /
  // `error.code` are still readable on AxiosError; the only behavioral
  // difference is the message text comes from axios ("Request failed with
  // status code 500") rather than the server's `data.message` field. Callers
  // that want the server message can read `error.response?.data?.message`.
  if (isBotProxy) {
    return Promise.reject(error);
  }

  const details: ApiErrorDetails | undefined = error?.response?.data?.details;

  const reduxError: Partial<SerializedError> = {
    message: error?.response?.data?.message,
    code: error?.response?.data?.errorCode || error?.response?.data?.code?.toString(),
    // PILOT-LOGIN-COM-ONLY: the collapse used to DROP `details`, so a typed payload could
    // never reach a slice — which is how both host gates get `details.allowedHost` to the
    // login slice. Additive — undefined for every error that carries none.
    details,
  };

  return Promise.reject(reduxError);
};

const api = Axios.create({
  baseURL: (window as any).app.config.apiBaseUrl
});

api.interceptors.request.use(requestInterceptor);
api.interceptors.response.use(response => response, responseErrorInterceptor);

export default api;